import React, { useContext, useMemo } from 'react'

import { Button, Snackbar, SnackbarContent, Typography } from '@mui/material'
import { Box, Icon } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import { useMobileMediaQuery, useSmallMediaQuery } from '@/utils/responsive'

import filterContext from '../context'

import styles from './styles.module.scss'
import { SNACKBAR_BOTTOM_GAP } from './types'

export const SeeSimilarSnackbar: React.FC = () => {
    const {
        dispatch: { onShowSimilarSectionsAction, onSetSelectSimilarSnackbarVisibility },
        selectors: { filteredListingsSelector, getLastSelectedSectionSelector },
        state: { showSelectSimilarSnackbar },
    } = useContext(filterContext)

    const isMobile = useMobileMediaQuery()
    const isSmallMobile = useSmallMediaQuery()

    const sectionName: string | undefined = useMemo(() => {
        if (getLastSelectedSectionSelector === undefined) {
            return ''
        }

        const listing = filteredListingsSelector.find(
            (listing) => listing.sectionId === getLastSelectedSectionSelector?.id,
        )
        return listing?.sectionLong
    }, [filteredListingsSelector, getLastSelectedSectionSelector])

    const handleClose = () => {
        onSetSelectSimilarSnackbarVisibility({ showSnackbar: false })
    }

    const handleSelectSimilar = () => {
        onShowSimilarSectionsAction()
    }

    const message = (
        <Box className={styles.snackbarMessage} data-testid="select-similar-snackbar-message">
            <Icon className={styles.snackbarIcon} type="state-success" size="md" />
            <div
                className={classNames(styles.sectionName, {
                    [styles.smallMobile]: isSmallMobile,
                })}
                data-testid="snackbar-section-name"
            >
                <Typography noWrap variant="body2">
                    {sectionName}
                </Typography>
            </div>
        </Box>
    )

    const action = (
        <Button
            color="primary"
            variant="text"
            onClick={handleSelectSimilar}
            data-testid="select-similar-snackbar-button"
            size="large"
        >
            View Similar Sections
        </Button>
    )

    // without some custom positioning, the snackbar covers privacy policy links
    // MUI's snackbar uses position: fixed and can't be overridden,
    // so transform/translate is our only option
    // this also needs to use the sx prop for styles, not a scss class
    const snackbarStyles = {
        transform: `translateY(${isMobile ? SNACKBAR_BOTTOM_GAP.MOBILE : SNACKBAR_BOTTOM_GAP.DESKTOP})`,
    }
    const snackbarContentStyles = { backgroundColor: '#fff', height: '56px', width: '380px', textOverflow: 'ellipsis' }
    return (
        <Snackbar
            open={showSelectSimilarSnackbar}
            autoHideDuration={7000}
            onClose={handleClose}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={snackbarStyles}
            data-testid="select-similar-snackbar"
        >
            <SnackbarContent sx={snackbarContentStyles} variant="elevation" message={message} action={action} />
        </Snackbar>
    )
}

export default SeeSimilarSnackbar
